import type { AccessLevel, PlanResponse } from '../api/types';

// The app's whole client state: which plan is open, its latest server-authoritative
// document, and the in-flight/error bookkeeping. Every mutation flows through the
// operations API and comes back as a fresh PlanResponse (ADR-025) — the reducer never
// edits the tree locally, it only swaps in what the server answered.

export interface PlanState {
  /** The open plan's id, or null on the welcome screen. */
  planId: string | null;
  /**
   * The open plan's name when known — the open response's `X-Resource-Name`, the listing, a
   * create, or a rename; null only where a host stamps none.
   */
  name: string | null;
  /** The latest server-authoritative document (null while nothing is open/loading). */
  plan: PlanResponse | null;
  /** The caller's effective access to the open plan (`X-Access-Level`, ADR-027 §7). */
  access: AccessLevel | null;
  /** True while a request is in flight (forms disable themselves). */
  busy: boolean;
  /**
   * True when a collaborator's rename notice was adopted while a request of ours was in flight
   * (ADR-028 §6): the request's own `plan-renamed` then only releases the forms, so a peer's
   * newer name — or our own echo, which the notice delivers first — is never overwritten by
   * the typed name the 204 confirms. Cleared by the next request.
   */
  peerRenamedInFlight: boolean;
  /**
   * The last failure — the error itself, worded at render time in the current language
   * (`describeError(error.cause, t.errors)`, ADR-031) so the banner follows a switch; null
   * when cleared.
   */
  error: { cause: unknown } | null;
}

export const initialState: PlanState = {
  planId: null,
  name: null,
  plan: null,
  access: null,
  busy: false,
  peerRenamedInFlight: false,
  error: null,
};

export type PlanAction =
  | { kind: 'request-started' }
  | {
      kind: 'plan-loaded';
      planId: string;
      plan: PlanResponse;
      access: AccessLevel;
      name: string | null;
    }
  | { kind: 'plan-updated'; plan: PlanResponse }
  | { kind: 'plan-refreshed'; plan: PlanResponse }
  | { kind: 'plan-renamed'; name: string }
  | { kind: 'plan-name-refreshed'; planId: string; name: string }
  | { kind: 'request-failed'; cause: unknown }
  | { kind: 'request-settled' }
  | { kind: 'error-cleared' }
  | { kind: 'plan-closed' };

export function planReducer(state: PlanState, action: PlanAction): PlanState {
  switch (action.kind) {
    case 'request-started':
      return { ...state, busy: true, peerRenamedInFlight: false, error: null };
    case 'plan-loaded':
      return {
        planId: action.planId,
        name: action.name,
        plan: action.plan,
        access: action.access,
        busy: false,
        peerRenamedInFlight: false,
        error: null,
      };
    case 'plan-updated':
      return { ...state, plan: action.plan, busy: false };
    case 'plan-refreshed':
      // A realtime-driven refetch (ADR-028 §6): adopt the document only if it is newer than
      // what we hold (an apply response may have overtaken it), and never touch `busy` —
      // it belongs to the request in flight, not to the subscription.
      return state.plan !== null &&
        state.plan.id === action.plan.id &&
        action.plan.version > state.plan.version
        ? { ...state, plan: action.plan }
        : state;
    case 'plan-renamed':
      // The caller's own rename came back: release the forms — and keep the name a notice
      // adopted meanwhile (our echo, or a peer's newer rename) over the typed one.
      return state.peerRenamedInFlight
        ? { ...state, busy: false, peerRenamedInFlight: false }
        : { ...state, name: action.name, busy: false };
    case 'plan-name-refreshed':
      // A collaborator's rename, as its realtime notice carried it (ADR-028 §3 as amended
      // 2026-09-16): adopt the name only for the open plan and only when it differs, and
      // never touch `busy` — it belongs to the request in flight, not to the subscription
      // (`plan-renamed` is the caller's own request and releases the forms; reusing it here
      // would re-enable them mid-request).
      return state.planId === action.planId && state.name !== action.name
        ? { ...state, name: action.name, peerRenamedInFlight: state.busy }
        : state;
    case 'request-failed':
      return { ...state, busy: false, error: { cause: action.cause } };
    case 'request-settled':
      // A request that changed nothing the reducer holds (a sharing mutation, a delete
      // that is about to close the plan) — just release the forms.
      return { ...state, busy: false };
    case 'error-cleared':
      return { ...state, error: null };
    case 'plan-closed':
      return initialState;
  }
}
